import { useCallback, useEffect, useRef, useState } from 'react'
import { useApolloClient, useLazyQuery } from '@apollo/client/react'

import { reloadPage } from '../../../../../../common/utils/urls'
import {
  AuthorizationStatus,
  Feature,
  Service,
} from '../../../../../../gql/graphql'
import type { WhatsAppChannelsQuery } from '../../../../../../gql/graphql'
import { WhatsAppConnectionInfo } from './query'
import {
  CompleteWhatsAppConnection,
  WhatsAppChannelStatus,
  WhatsAppChannels,
} from './mutation'
import { MetaSdkRequiresReloadError, loadMetaSdk } from './metaSdk'
import type { MetaSdk, WhatsAppConfiguration } from './metaSdk'
import { startWhatsAppSignup } from './startWhatsAppSignup'
import type {
  WhatsAppFinishType,
  WhatsAppSignupResult,
} from './startWhatsAppSignup'

export const CONNECTION_ERROR =
  'We couldn’t connect WhatsApp. Please try again.'

type ReadyState = {
  status: 'ready'
  sdk: MetaSdk
  configuration: WhatsAppConfiguration
}
type SignupState =
  | { status: 'loading' | 'pending' | 'connected' | 'syncing' }
  | { status: 'connecting'; finishType: WhatsAppFinishType }
  | ReadyState
  | { status: 'error'; message: string; requiresReload?: boolean }
  | { status: 'needsRepair' | 'saved' }
type WhatsAppChannel = WhatsAppChannelsQuery['channels'][number]

// Community treats the same statuses as fixed by a reconnect.
const REPAIRABLE_STATUSES: ReadonlySet<AuthorizationStatus> = new Set([
  AuthorizationStatus.NeedsRefresh,
  AuthorizationStatus.NotEnoughData,
])

export function useWhatsAppSignup(
  organizationId: string,
  channelId?: string,
): {
  state: SignupState
  prepare: () => Promise<void>
  start: () => void
} {
  const client = useApolloClient()
  const [getInfo] = useLazyQuery(WhatsAppConnectionInfo, {
    fetchPolicy: 'network-only',
  })
  const [state, setState] = useState<SignupState>({ status: 'loading' })
  // A result other than ok saved a Channel, so the next signup reconnects it
  // instead of counting against the Channel limit.
  const savedChannelId = useRef<string>(undefined)
  const request = useRef(0)
  const dispose = useRef<(() => void) | null>(null)

  const prepare = useCallback(async (): Promise<void> => {
    const currentRequest = ++request.current
    dispose.current?.()
    dispose.current = null
    setState({ status: 'loading' })
    try {
      const response = await getInfo({
        variables: {
          input: {
            organizationId,
            channelId: channelId ?? savedChannelId.current,
          },
        },
      })
      if (currentRequest !== request.current) return
      const configuration = response.data?.whatsAppConnectionInfo
      if (configuration?.__typename !== 'WhatsAppConnectionInfoResponse') {
        setState({
          status: 'error',
          message:
            configuration?.message ||
            'We couldn’t start WhatsApp signup. Please try again.',
        })
        return
      }
      const sdk = await loadMetaSdk()
      if (currentRequest === request.current) {
        setState({ status: 'ready', sdk, configuration })
      }
    } catch (error) {
      if (currentRequest === request.current) {
        const requiresReload = error instanceof MetaSdkRequiresReloadError
        setState({
          status: 'error',
          message: requiresReload
            ? 'We couldn’t load WhatsApp signup. Refresh this page and try again.'
            : 'We couldn’t start WhatsApp signup. Please try again.',
          requiresReload,
        })
      }
    }
  }, [getInfo, organizationId, channelId])

  const discard = useCallback((): void => {
    request.current++
    dispose.current?.()
    dispose.current = null
  }, [])

  useEffect(() => {
    savedChannelId.current = undefined
    void prepare()
    return discard
  }, [prepare, discard])

  async function findSavedChannel(
    phoneNumberId: string,
  ): Promise<WhatsAppChannel | undefined> {
    try {
      const { data } = await client.query({
        query: WhatsAppChannels,
        variables: { input: { organizationId } },
        fetchPolicy: 'network-only',
      })
      return data?.channels.find(
        (channel) =>
          channel.service === Service.Whatsapp &&
          channel.serviceId === phoneNumberId,
      )
    } catch {
      return undefined
    }
  }

  async function getDirectMessageStatus(
    savedId: string,
  ): Promise<AuthorizationStatus | undefined> {
    try {
      const { data } = await client.query({
        query: WhatsAppChannelStatus,
        variables: { input: { organizationId } },
        fetchPolicy: 'network-only',
      })
      return data?.configuration.channels
        .find((config) => config.channelId === savedId)
        ?.authorizationStatus.find(
          (authorization) => authorization.feature === Feature.DirectMessage,
        )?.status
    } catch {
      return undefined
    }
  }

  async function complete(result: WhatsAppSignupResult): Promise<void> {
    const currentRequest = request.current
    setState({ status: 'connecting', finishType: result.finishType })
    let channel: WhatsAppChannel | undefined
    try {
      const { data } = await client.mutate({
        mutation: CompleteWhatsAppConnection,
        variables: {
          input: {
            code: result.code,
            wabaId: result.wabaId,
            phoneNumberId: result.phoneNumberId,
            organizationId,
            channelId: channelId ?? savedChannelId.current,
          },
        },
      })
      const payload = data?.completeWhatsAppConnection
      if (currentRequest !== request.current) return
      if (payload?.__typename === 'CompleteWhatsAppConnectionResponse') {
        channel = payload.channel
      } else if (payload) {
        setState({ status: 'error', message: payload.message })
        return
      }
    } catch {
      // The server can save the Channel and still fail to answer, and the
      // signup code works once, so check before asking for a new signup.
    }
    if (!channel) {
      channel = await findSavedChannel(result.phoneNumberId)
      if (currentRequest !== request.current) return
    }
    if (!channel) {
      setState({ status: 'error', message: CONNECTION_ERROR })
      return
    }
    const status = await getDirectMessageStatus(channel.id)
    if (currentRequest !== request.current) return
    if (status === AuthorizationStatus.Ok) {
      if (result.finishType === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        setState({ status: 'syncing' })
        return
      }
      setState({ status: 'connected' })
      reloadPage()
      return
    }
    savedChannelId.current = channel.id
    setState({
      status:
        status && REPAIRABLE_STATUSES.has(status) ? 'needsRepair' : 'saved',
    })
  }

  function start(): void {
    if (state.status !== 'ready' || dispose.current) return
    const ready = state
    setState({ status: 'pending' })
    const currentRequest = request.current
    let settled = false
    function finish(nextState: SignupState): void {
      settled = true
      dispose.current = null
      if (currentRequest === request.current) setState(nextState)
    }
    dispose.current = (): void => {}
    const disposeSignup = startWhatsAppSignup({
      sdk: ready.sdk,
      configuration: ready.configuration,
      onResult: (result) => {
        settled = true
        dispose.current = null
        if (currentRequest === request.current) void complete(result)
      },
      onError: (message, requiresReload) => {
        finish({ status: 'error', message, requiresReload })
      },
      onIncomplete: () => {
        finish(ready)
      },
    })
    if (!settled) dispose.current = disposeSignup
  }

  return { state, prepare, start }
}
